import { Field, FieldContent, FieldDescription, FieldLabel } from "@/components/ui/field"
import { Switch } from "@/components/ui/switch"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import {
  levels,
  serviceLevel,
  services,
  withServiceLevel,
  type Level,
  type Service
} from "../../../../shared/permissions"

const serviceNames: Record<Service, string> = {
  authoring: "Authoring",
  standards: "Standards",
  registry: "Experience Registry"
}

const levelNames: Record<Level | "none", string> = {
  none: "No access",
  read: "Read",
  edit: "Edit",
  admin: "Admin"
}

/**
 * A user's roles as a grid: the global admin switch, then one row of level buttons per service.
 * Rows the actor can't assign stay visible but disabled.
 */
export function AccessGrid({
  id,
  roles,
  onChange,
  canSetAdmin,
  editable
}: {
  id: string
  roles: string[]
  onChange: (roles: string[]) => void
  /** Whether the global admin switch can be changed: only global admins assign it. */
  canSetAdmin: boolean
  /** Services whose row can be changed. */
  editable: readonly Service[]
}) {
  const isAdmin = roles.includes("admin")
  const set = (next: string[]) =>
    onChange(next.length ? next : ["user"])

  return (
    <div className="flex flex-col gap-4">
      <Field orientation="horizontal">
        <Switch
          checked={isAdmin}
          disabled={!canSetAdmin}
          id={`${id}-admin`}
          onCheckedChange={(checked) =>
            set(
              checked
                ? [...roles, "admin"]
                : roles.filter((role) => role !== "admin")
            )
          }
        />
        <FieldContent>
          <FieldLabel htmlFor={`${id}-admin`}>Global admin</FieldLabel>
          <FieldDescription>
            Manages staff and has full access to every service.
          </FieldDescription>
        </FieldContent>
      </Field>
      <div
        className="grid grid-cols-[auto_1fr] items-center gap-x-6 gap-y-2 rounded-lg border p-3"
        role="group"
        aria-label="Service access"
      >
        {services.map((service) => {
          const level = serviceLevel(roles.join(","), service)
          const disabled = !editable.includes(service)
          return (
            <div className="contents" key={service}>
              <span
                className={`text-sm font-medium ${disabled ? "text-muted-foreground" : ""}`}
                id={`${id}-${service}`}
              >
                {serviceNames[service]}
              </span>
              <ToggleGroup
                aria-labelledby={`${id}-${service}`}
                className="w-full"
                disabled={disabled}
                onValueChange={(value) =>
                  // Clicking the selected button would clear it; keep a level selected.
                  value &&
                  set(
                    withServiceLevel(
                      roles,
                      service,
                      value === "none" ? null : (value as Level)
                    )
                  )
                }
                size="sm"
                spacing={0}
                type="single"
                value={level ?? "none"}
                variant="outline"
              >
                {(["none", ...levels] as const).map((item) => (
                  <ToggleGroupItem
                    className="flex-1 px-3 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                    key={item}
                    value={item}
                  >
                    {levelNames[item]}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </div>
          )
        })}
      </div>
      {isAdmin ? (
        <p className="text-xs text-muted-foreground">
          As a global admin, this user already has full access to every
          service whatever is selected above.
        </p>
      ) : null}
    </div>
  )
}
